/**
 * IMPORT APOGEE — appariement des colonnes et contrôle de forme
 * =============================================================
 * Spec     : doc/modules/etudiants.md §0 (ce que les fichiers réels imposent)
 * Maquette : doc/maquettes/etudiants_maquette_v5.html, onglet 2
 * Banc     : doc/jeux_essai/ — 147 étudiants anonymisés, 6 cas limites
 *
 * Module PUR : aucune dépendance à React ni au réseau. C'est ce qui le rend
 * éprouvable sans cliquer, et c'est là que vit toute la connaissance du format
 * du fichier de la scolarité.
 *
 * ── POURQUOI CE FICHIER EXISTE (§0.8) ────────────────────────────────────────
 * Le fichier `Liste_Scolarite` est la CONCATÉNATION DE DEUX EXTRACTIONS : 80 lignes
 * conformes aux en-têtes, 67 décalées d'un cran, entrelacées. Un mapping par égalité
 * de libellé comme un mapping par position écrivent des données fausses pour 46 % de
 * l'effectif. La réponse est en trois temps :
 *
 *   1. ancre par intitulé NORMALISÉ (casse, accents, espaces) ;
 *   2. VALIDATION DE FORME par champ — une colonne dont plus de 5 % des valeurs non
 *      vides échouent est REFUSÉE, pas devinée ;
 *   3. PRÉVISUALISATION avant écriture : libellé source, champ cible, taux de
 *      conformité, trois exemples.
 *
 * ── ET POURQUOI IL N'Y A AUCUN RATTRAPAGE DU DÉCALAGE ────────────────────────
 * Mesuré (doc/jeux_essai/generer_jeux_essai.js, construction de la feuille APOGEE) :
 * le décalage du groupe 2 ne touche QUE trois colonnes — code postal, ville,
 * téléphone fixe. Le portable et l'e-mail personnel sont au MÊME endroit dans les
 * deux extractions, conformes 147/147.
 *
 * Or ces trois colonnes sont exactement celles que le RGPD écarte (§0.6). La LISTE
 * BLANCHE ci-dessous annule donc le problème du décalage : il n'y a rien à rattraper,
 * et un rattrapage serait le devineur que le §0.8 interdit — un attribut qu'on devine
 * est une notion manquante.
 *
 * ── CE QUI N'ENTRE PAS EN BASE (§0.6) ────────────────────────────────────────
 * Liste BLANCHE, jamais liste noire : 18 colonnes lues sur 30. Ne sont pas lues, et
 * ne quittent donc jamais le poste puisque la lecture se fait dans le navigateur :
 * date de création de l'inscription, lieu et département de naissance, adresse,
 * code postal, ville, téléphone fixe, « PORTABLE  - Ligne 1 » (vide sur 147),
 * « Bac - Mention (code) » (mixte, inexploitable), DEPARTEMENT BAC, ANNEE DERNIERE.
 * Rien n'est déversé dans `donnees_brutes` : y ranger le lieu de naissance « au cas
 * où » serait contourner la question au lieu d'y répondre.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// 1. NORMALISATION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Intitulé de colonne ramené à sa forme comparable : minuscules, accents dépliés,
 * espaces multiples réduits, espaces de bord retirés.
 *
 * C'est ce qui rend survivables les pièges de frappe MESURÉS dans le fichier réel :
 * l'espace final de « LIEU NAISSANCE », le double espace de « PORTABLE  - Ligne 1 »,
 * et les cinq colonnes en majuscules quand les autres sont en casse normale (§0.3).
 * Écart E12 : la note « sensible à la casse » de la maquette était fausse.
 */
export function normaliserIntitule(valeur) {
    return String(valeur ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')   // dépile les accents
        // Apostrophe typographique ramenée à l'apostrophe droite : le fichier réel
        // porte U+0027 sur « Redoublant à l'étape », mesuré — mais une réexportation
        // depuis un Excel réglé autrement produirait U+2019, et l'appariement
        // échouerait sur une différence invisible à l'œil.
        .replace(/[‘’ʼ]/g, "'")
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}

/** Cellule ramenée à une chaîne utile. `null`, `undefined` et les espaces valent vide. */
function texte(brut) {
    if (brut === null || brut === undefined) return '';
    if (brut instanceof Date) return brut.toISOString().slice(0, 10);
    return String(brut).replace(/\s+/g, ' ').trim();
}

const estVide = (brut) => texte(brut) === '';

// ═══════════════════════════════════════════════════════════════════════════════
// 2. CONVERSIONS — les trois formes de date, les O/N, les nombres
// ═══════════════════════════════════════════════════════════════════════════════

/** Bornes plausibles d'un serial Excel pour une DATE DE NAISSANCE : 1920 → 2036. */
const SERIAL_MIN = 7305;
const SERIAL_MAX = 50000;

/**
 * Date de naissance — TROIS formes à accepter, mesurées (§0 et banc de test §5) :
 *   · serial Excel AVEC format de date   → groupe 1 du fichier (80 lignes)
 *   · serial Excel SANS format de date   → groupe 2 (67 lignes) : `cellDates` de
 *     SheetJS ne le convertit pas, puisqu'il se fie au format de la cellule
 *   · texte « jj/mm/aaaa »               → mode « coller », et export ScoDoc
 *
 * Rend `YYYY-MM-DD` (ce que PostgreSQL attend) ou `null` si la forme est refusée.
 */
export function convertirDate(brut) {
    if (brut === null || brut === undefined || brut === '') return null;

    // Forme 1 et 2 — serial Excel. Base 1899-12-30 : au-delà du serial 60, cette
    // origine absorbe le faux 29 février 1900 d'Excel.
    if (typeof brut === 'number' || (typeof brut === 'string' && /^\d{4,5}(\.\d+)?$/.test(brut.trim()))) {
        const serial = Math.floor(Number(brut));
        if (serial < SERIAL_MIN || serial > SERIAL_MAX) return null;
        const d = new Date(Date.UTC(1899, 11, 30) + serial * 86400000);
        return d.toISOString().slice(0, 10);
    }

    // SheetJS rend un objet Date quand `cellDates` est actif et la cellule formatée.
    if (brut instanceof Date) {
        if (Number.isNaN(brut.getTime())) return null;
        return new Date(Date.UTC(brut.getFullYear(), brut.getMonth(), brut.getDate()))
            .toISOString().slice(0, 10);
    }

    // Forme 3 — texte. jj/mm/aaaa, jj-mm-aaaa, et aaaa-mm-jj déjà normalisé.
    const t = texte(brut);
    let m = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/.exec(t);
    if (m) {
        const [, j, mo, a] = m.map(Number);
        return dateValide(a, mo, j);
    }
    m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
    if (m) {
        const [, a, mo, j] = m.map(Number);
        return dateValide(a, mo, j);
    }
    return null;
}

/** Refuse le 31 février : `new Date` le décalerait en silence au 3 mars. */
function dateValide(a, mo, j) {
    if (mo < 1 || mo > 12 || j < 1 || j > 31) return null;
    const d = new Date(Date.UTC(a, mo - 1, j));
    if (d.getUTCFullYear() !== a || d.getUTCMonth() !== mo - 1 || d.getUTCDate() !== j) return null;
    return d.toISOString().slice(0, 10);
}

/**
 * « Boursier (O/N) » et « Redoublant à l'étape (O/N/NA) ».
 * `NA` n'est ni oui ni non : c'est une absence d'information, et elle rend `null`
 * plutôt que `false`. Confondre les deux ferait dire au fichier ce qu'il ne dit pas.
 */
export function convertirOuiNon(brut) {
    const t = texte(brut).toUpperCase();
    if (t === 'O' || t === 'OUI' || t === '1' || t === 'TRUE') return true;
    if (t === 'N' || t === 'NON' || t === '0' || t === 'FALSE') return false;
    return null;
}

/** Entier borné, ou `null`. Les cellules numériques d'Excel arrivent en nombre. */
export function convertirEntier(brut, min, max) {
    const t = texte(brut).replace(/\s/g, '');
    if (!/^-?\d+$/.test(t)) return null;
    const n = parseInt(t, 10);
    if (n < min || n > max) return null;
    return n;
}

/**
 * Téléphone ramené au format que la maquette de la fiche affiche : `06 64 85 83 76`.
 * Les six motifs MESURÉS dans le fichier réel (banc de test) sont acceptés :
 * `+336…`, `+3306…`, `0X XX XX XX XX`, `0X XXXXXXXX`, `0XXX XX XX XX`, `0XXXXXXXXX`.
 */
export function convertirTelephone(brut) {
    const brut2 = texte(brut).replace(/[\s.\-()]/g, '');
    let m = /^(?:\+33|0033)0?([1-9]\d{8})$/.exec(brut2) || /^0([1-9]\d{8})$/.exec(brut2);
    if (!m) return null;
    const neuf = m[1];
    return '0' + neuf.replace(/(\d)(\d\d)(\d\d)(\d\d)(\d\d)/, '$1 $2 $3 $4 $5');
}

// ═══════════════════════════════════════════════════════════════════════════════
// 3. LA LISTE BLANCHE — 18 colonnes lues sur 30
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Forme attendue du NUMÉRO ÉTUDIANT. Mesuré : 8 chiffres, 147/147, aucun doublon.
 *
 * ⚠️ Volontairement une constante nommée, et non un littéral perdu dans un test :
 * Pégase remplace progressivement Apogée et apportera son propre format
 * (`code_pegase_apprenant`, §0.7). « 8 chiffres » n'est donc pas une vérité gravée,
 * c'est un contrôle de forme, et c'est ICI qu'on le change.
 */
export const FORME_NUMERO_ETUDIANT = /^\d{8}$/;

/** Au-delà de 5 % de valeurs non vides non conformes, la colonne est refusée (§0.8). */
export const SEUIL_CONFORMITE = 0.95;

/**
 * PLANCHER qui accompagne le seuil de 5 %, et sans lequel la règle du §0.8 se
 * retourne contre elle-même sur les petits fichiers.
 *
 * Trouvé au banc, sur la feuille `6_ligne_corrompue` : quatre lignes, une seule
 * cellule fautive (une version d'étape « ABC ») font 25 % d'échec. La colonne entière
 * était donc refusée, et les TROIS lignes saines tombaient avec elle — alors que le
 * banc attend l'inverse : « l'import doit refuser la fautive et continuer ».
 *
 * Ce que le contrôle de forme cherche, c'est une colonne SYSTÉMATIQUEMENT mal
 * appariée, et celle-là se trahit par un échec massif : mesuré, la colonne « Ville »
 * du groupe 2 contient un code postal sur 67 lignes sur 67. Une ou deux cellules
 * fautives sont des erreurs de saisie, qui se refusent LIGNE À LIGNE.
 *
 * Une colonne n'est donc refusée que si les deux conditions tiennent : plus de 5 %
 * d'échecs ET au moins trois valeurs non conformes.
 */
export const PLANCHER_NON_CONFORMES = 3;

const REGIMES_FICHIER = {
    'initiale': 'initiale',
    'apprentis': 'apprentis',
    'fc sans cp': 'fc',
    'formation continue': 'fc'
};

/**
 * Une entrée par colonne LUE. Les colonnes absentes de cette liste ne sont ni lues,
 * ni transmises au serveur, ni stockées.
 *
 *   intitule    l'en-tête MESURÉ dans le fichier réel, au caractère près
 *   cible       le champ de la base
 *   table       'personne' (etudiants) · 'inscription' (etudiants_inscriptions)
 *               · 'controle' (lu, jamais écrit — sert à recouper)
 *   forme       la forme attendue, en français : c'est ce que la prévisualisation
 *               affiche quand une colonne est refusée
 */
export const CHAMPS_APOGEE = [
    // ── La clé de liaison ────────────────────────────────────────────────────
    // Son intitulé n'est PAS pris ici mais dans `settings.apogee_cle_liaison`
    // (onglet Paramétrage) : c'est le seul intitulé configurable. Écart E11 : la
    // clé est le numéro étudiant, pas l'INE — 59/61 appariements, 0 contradiction.
    {
        cle: 'code_etudiant', intitule: 'Individu - Code Etudiant',
        cible: 'code_etudiant', table: 'personne', role: 'cle',
        obligatoire: true, forme: '8 chiffres',
        valider: (b) => FORME_NUMERO_ETUDIANT.test(texte(b)),
        convertir: (b) => texte(b)
    },

    // ── Identité ─────────────────────────────────────────────────────────────
    {
        cle: 'ine', intitule: 'Identifiant National INE (code+clé)',
        cible: 'ine', table: 'personne',
        obligatoire: false, forme: '9 chiffres puis 2 lettres',
        // Le libellé « code+clé » ne signifie pas une longueur différente : mesuré,
        // les deux colonnes INE ont le même format (§0.4).
        valider: (b) => /^\d{9}[A-Za-z]{2}$/.test(texte(b)),
        convertir: (b) => texte(b).toUpperCase()
    },
    {
        cle: 'nom', intitule: 'Individu - Nom',
        cible: 'nom', table: 'personne',
        obligatoire: true, forme: 'texte non vide',
        valider: (b) => texte(b).length > 0 && texte(b).length <= 100,
        convertir: (b) => texte(b)
    },
    {
        cle: 'prenom', intitule: 'Individu - Prénom',
        cible: 'prenom', table: 'personne',
        obligatoire: true, forme: 'texte non vide',
        valider: (b) => texte(b).length > 0 && texte(b).length <= 100,
        convertir: (b) => texte(b)
    },
    {
        cle: 'sexe', intitule: 'Individu - Sexe',
        cible: 'sexe', table: 'personne',
        obligatoire: false, forme: 'M ou F',
        valider: (b) => /^[MF]$/i.test(texte(b)),
        convertir: (b) => texte(b).toUpperCase()
    },
    {
        cle: 'date_naissance', intitule: 'Naissance - Date',
        cible: 'date_naissance', table: 'personne',
        obligatoire: false, forme: 'date (jj/mm/aaaa ou date Excel)',
        valider: (b) => convertirDate(b) !== null,
        convertir: convertirDate
    },

    // ── Contact — les deux seules colonnes de contact conformes 147/147 ───────
    {
        cle: 'telephone', intitule: 'Individu - Tél. portable',
        cible: 'telephone', table: 'personne',
        obligatoire: false, forme: 'téléphone français',
        valider: (b) => convertirTelephone(b) !== null,
        convertir: convertirTelephone
    },
    {
        cle: 'email', intitule: 'Individu - Email personnel',
        cible: 'email', table: 'personne',
        obligatoire: false, forme: 'adresse e-mail',
        valider: (b) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(texte(b)),
        convertir: (b) => texte(b).toLowerCase()
    },

    // ── Bac — DEUX en-têtes qui ne décrivent pas leur contenu (§0.3) ──────────
    {
        cle: 'type_bac', intitule: 'Bac ou équivalence (lib.)',
        cible: 'type_bac', table: 'personne',
        obligatoire: false, forme: 'texte non vide',
        valider: (b) => texte(b).length > 0 && texte(b).length <= 50,
        convertir: (b) => texte(b)
    },
    {
        // L'en-tête annonce un établissement, la colonne contient LA MENTION.
        // Mesuré 146/146 dans P / AB / B / TB.
        cle: 'mention_bac', intitule: 'Bac - Etab. préparation (lib.)',
        cible: 'mention_bac', table: 'personne',
        obligatoire: false, forme: 'P, AB, B ou TB',
        valider: (b) => /^(P|AB|B|TB)$/i.test(texte(b)),
        convertir: (b) => texte(b).toUpperCase()
    },
    {
        // L'en-tête annonce un département, la colonne contient L'ANNÉE DU BAC.
        // Mesuré 147/147.
        cle: 'annee_bac', intitule: 'Bac - Département obtention (lib.)',
        cible: 'annee_bac', table: 'personne',
        obligatoire: false, forme: 'année à 4 chiffres',
        valider: (b) => convertirEntier(b, 1990, 2035) !== null,
        convertir: (b) => convertirEntier(b, 1990, 2035)
    },
    {
        cle: 'cursus', intitule: 'Situation année précédente (lib.)',
        cible: 'cursus', table: 'personne',
        obligatoire: false, forme: 'texte',
        valider: (b) => texte(b).length > 0,
        convertir: (b) => texte(b)
    },

    // ── L'inscription de l'année ──────────────────────────────────────────────
    {
        cle: 'code_etape', intitule: 'IAE - Etape (code)',
        cible: 'code_etape', table: 'inscription',
        obligatoire: true, forme: '3 à 20 lettres ou chiffres',
        valider: (b) => /^[A-Za-z0-9]{3,20}$/.test(texte(b)),
        convertir: (b) => texte(b).toUpperCase()
    },
    {
        cle: 'version_etape', intitule: "IAE - Version d'étape (code)",
        cible: 'version_etape', table: 'inscription',
        obligatoire: true, forme: 'de 1 à 10 chiffres',
        valider: (b) => /^\d{1,10}$/.test(texte(b)),
        convertir: (b) => texte(b)
    },
    {
        // LU, JAMAIS ÉCRIT. Le régime vient du MAPPING, qui est la déclaration de
        // l'utilisateur et porte déjà les trois valeurs du CHECK de la v38. Cette
        // colonne sert à RECOUPER : si elle contredit le mapping, la ligne passe et
        // l'écart est signalé au bilan. Décidé le 2026-08-16.
        // Motif : le fichier écrit « FC sans CP », valeur hors CHECK, dont la
        // traduction en dur serait un devineur caché dans le code.
        cle: 'regime_fichier', intitule: "Régime d'inscription (lib.)",
        cible: null, table: 'controle',
        obligatoire: false, forme: 'initiale, apprentis ou FC sans CP',
        valider: (b) => REGIMES_FICHIER[normaliserIntitule(b)] !== undefined,
        convertir: (b) => REGIMES_FICHIER[normaliserIntitule(b)] || null
    },
    {
        cle: 'redoublant', intitule: "Redoublant à l'étape (O/N/NA)",
        cible: 'redoublant', table: 'inscription',
        obligatoire: false, forme: 'O, N ou NA',
        valider: (b) => convertirOuiNon(b) !== null || texte(b).toUpperCase() === 'NA',
        convertir: convertirOuiNon
    },
    {
        cle: 'boursier', intitule: 'Boursier (O/N)',
        cible: 'boursier', table: 'inscription',
        obligatoire: false, forme: 'O ou N',
        valider: (b) => convertirOuiNon(b) !== null,
        convertir: convertirOuiNon
    },
    {
        // Le classement APOGEE est le rang dans la promotion INSCRITE (1 à 66).
        // Il n'a rien à voir avec celui de SCODOC (11 à 210, à l'admission) :
        // mesuré, ils diffèrent sur 47 cas sur 48. D'où deux champs distincts.
        cle: 'classement_admission', intitule: 'Classement',
        cible: 'classement_admission', table: 'inscription',
        obligatoire: false, forme: 'entier de 1 à 999',
        valider: (b) => convertirEntier(b, 1, 999) !== null,
        convertir: (b) => convertirEntier(b, 1, 999)
    }
];

/** Les 12 colonnes délibérément NON lues, pour que la prévisualisation le dise. */
export const COLONNES_NON_LUES = [
    { intitule: 'Date création IA (date)',        motif: 'sans usage dans l\'application' },
    { intitule: 'LIEU NAISSANCE',                 motif: 'donnée personnelle sans usage (RGPD)' },
    { intitule: 'DEPARTEMENT NAISSANCE',          motif: 'donnée personnelle sans usage (RGPD)' },
    { intitule: 'Adresse annuelle - Ligne 2',     motif: 'adresse postale non stockée (RGPD)' },
    { intitule: 'Adresse annuelle - Ligne 3',     motif: 'adresse postale non stockée (RGPD)' },
    { intitule: 'Adresse annuelle - Code postal', motif: 'adresse postale non stockée (RGPD) — et colonne décalée sur 67 lignes' },
    { intitule: 'Adresse annuelle - Ville',       motif: 'adresse postale non stockée (RGPD) — et colonne décalée sur 67 lignes' },
    { intitule: 'Adresse fixe - Téléphone',       motif: 'téléphone fixe non stocké (RGPD) — et colonne décalée sur 67 lignes' },
    { intitule: 'PORTABLE  - Ligne 1',            motif: 'vide sur les 147 lignes du fichier réel' },
    { intitule: 'Bac - Mention (code)',           motif: 'contenu mixte : série sur 80 lignes, mention sur 67 — inexploitable' },
    { intitule: 'DEPARTEMENT BAC',                motif: 'sans usage dans l\'application' },
    { intitule: 'ANNEE DERNIERE',                 motif: 'doublon de « Situation année précédente »' }
];

const PAR_CLE = CHAMPS_APOGEE.reduce((acc, c) => { acc[c.cle] = c; return acc; }, {});

// ═══════════════════════════════════════════════════════════════════════════════
// 4. APPARIEMENT — temps 1 du §0.8
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Apparie les en-têtes du fichier aux champs de la liste blanche, par intitulé
 * NORMALISÉ. L'intitulé de la colonne clé vient du paramétrage.
 *
 * @param {Array} entetes    la première ligne du fichier, telle que lue
 * @param {string} cleLiaison  `settings.apogee_cle_liaison`
 * @returns {{colonnes: Array, cleManquante: boolean, obligatoiresManquants: Array,
 *            nonLues: Array, inconnues: Array}}
 */
export function apparierColonnes(entetes, cleLiaison) {
    const normalisees = (entetes || []).map(normaliserIntitule);
    const utilisees = new Set();

    const colonnes = CHAMPS_APOGEE.map((champ) => {
        // La colonne clé se cherche par l'intitulé CONFIGURÉ ; les autres par leur
        // intitulé mesuré. Un seul intitulé est paramétrable, et c'est voulu :
        // dix-huit réglages seraient dix-huit occasions de se tromper.
        const attendu = champ.role === 'cle' && cleLiaison
            ? normaliserIntitule(cleLiaison)
            : normaliserIntitule(champ.intitule);

        let index = normalisees.findIndex((h, i) => h === attendu && !utilisees.has(i));
        if (index >= 0) utilisees.add(index);

        return {
            cle: champ.cle,
            intituleAttendu: champ.role === 'cle' && cleLiaison ? cleLiaison : champ.intitule,
            intituleSource: index >= 0 ? String(entetes[index] ?? '') : null,
            index: index >= 0 ? index : null,
            cible: champ.cible,
            table: champ.table,
            role: champ.role || 'donnee',
            forme: champ.forme,
            obligatoire: champ.obligatoire,
            trouvee: index >= 0
        };
    });

    // Les en-têtes du fichier qu'aucun champ n'a réclamés : soit une colonne
    // délibérément non lue (RGPD, §0.6), soit une colonne que nous ne connaissons
    // pas — et le §0.7 impose de TOLÉRER l'ajout de colonnes, jamais de le refuser.
    const nonLues = [];
    const inconnues = [];
    (entetes || []).forEach((h, i) => {
        if (utilisees.has(i)) return;
        const brut = String(h ?? '');
        if (normaliserIntitule(brut) === '') return;   // la 41ᵉ colonne sans nom
        const connue = COLONNES_NON_LUES.find(
            (c) => normaliserIntitule(c.intitule) === normaliserIntitule(brut)
        );
        (connue ? nonLues : inconnues).push({
            intitule: brut,
            motif: connue ? connue.motif : 'colonne inconnue de la liste blanche — non lue'
        });
    });

    const cle = colonnes.find((c) => c.role === 'cle');
    return {
        colonnes,
        cleManquante: !cle || !cle.trouvee,
        obligatoiresManquants: colonnes.filter((c) => c.obligatoire && !c.trouvee),
        nonLues,
        inconnues
    };
}

// ═══════════════════════════════════════════════════════════════════════════════
// 5. CONTRÔLE DE FORME — temps 2 du §0.8
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Pour chaque colonne appariée : combien de valeurs non vides, combien conformes,
 * le taux, et TROIS EXEMPLES. Une colonne sous le seuil est marquée `refusee` :
 * elle ne sera pas écrite, et la prévisualisation dit laquelle et pourquoi.
 *
 * @param {Array} colonnes  sortie de `apparierColonnes`
 * @param {Array} lignes    lignes de données, chacune `{numero, cellules}`
 */
export function controlerColonnes(colonnes, lignes) {
    return colonnes.map((col) => {
        if (!col.trouvee) return { ...col, nbNonVides: 0, nbConformes: 0, taux: null, exemples: [], refusee: false };

        const champ = PAR_CLE[col.cle];
        let nbNonVides = 0;
        let nbConformes = 0;
        const exemples = [];
        const contreExemples = [];

        for (const ligne of lignes) {
            const brut = ligne.cellules[col.index];
            if (estVide(brut)) continue;
            nbNonVides++;
            if (champ.valider(brut)) {
                nbConformes++;
                if (exemples.length < 3) {
                    // La valeur SOURCE et la valeur qui sera ÉCRITE. Les deux, parce
                    // qu'elles diffèrent là où ça compte : une date de naissance arrive
                    // en serial Excel (« 39043 ») et un portable sous six formes
                    // (« +33684807585 »). Une prévisualisation qui n'affiche que la
                    // source ne prévisualise pas l'import — trouvé à la relecture de
                    // l'image, où la colonne des dates ne montrait que des nombres.
                    const source = texte(brut);
                    const ecrit = String(champ.convertir(brut) ?? '');
                    exemples.push({ source, ecrit, converti: ecrit !== source });
                }
            } else if (contreExemples.length < 3) {
                contreExemples.push({ ligne: ligne.numero, valeur: texte(brut) });
            }
        }

        const taux = nbNonVides === 0 ? null : nbConformes / nbNonVides;
        const nbNonConformes = nbNonVides - nbConformes;

        // Une colonne vide n'est pas une colonne fausse : « PORTABLE  - Ligne 1 » est
        // vide sur 147 lignes, et le classement APOGEE n'est renseigné que pour 71
        // étudiants. Le taux se mesure sur les valeurs NON VIDES, jamais sur le total.
        const refusee = taux !== null
            && taux < SEUIL_CONFORMITE
            && nbNonConformes >= PLANCHER_NON_CONFORMES;

        return { ...col, nbNonVides, nbConformes, nbNonConformes, taux, exemples, contreExemples, refusee };
    });
}

// ═══════════════════════════════════════════════════════════════════════════════
// 6. PRÉPARATION DES LIGNES À TRANSMETTRE
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Convertit les lignes du fichier en objets prêts pour le serveur, en n'emportant
 * QUE la liste blanche et QUE les colonnes non refusées.
 *
 * Les lignes dont la clé manque ou dont un champ obligatoire est vide sont écartées
 * ICI, avec leur numéro de ligne du fichier : un échec d'import doit remonter à
 * l'écran avec la ligne fautive, jamais dans un `console.error`.
 *
 * @returns {{lignes: Array, refus: Array, doublonsInternes: Array, avertissements: Array}}
 */
export function preparerLignes(colonnes, lignes) {
    const retenues = colonnes.filter((c) => c.trouvee && !c.refusee);
    const preparees = [];
    const refus = [];
    const doublonsInternes = [];
    const avertissements = [];
    const vues = new Map();   // code_etudiant -> numéro de la première ligne

    for (const ligne of lignes) {
        const objet = { ligne: ligne.numero };
        // Champs présents dans le fichier mais REJETÉS par le contrôle de forme.
        // Distinguer « absent » de « mal formé » n'est pas un raffinement : c'est la
        // différence entre « la scolarité a oublié une valeur » et « la colonne ne
        // contient pas ce que son en-tête annonce ».
        const malFormes = new Map();

        for (const col of retenues) {
            const brut = ligne.cellules[col.index];
            if (estVide(brut)) continue;
            const champ = PAR_CLE[col.cle];
            if (!champ.valider(brut)) {
                malFormes.set(col.cle, { valeur: texte(brut), forme: col.forme, source: col.intituleSource });
                continue;
            }
            objet[col.cle] = champ.convertir(brut);
        }

        /** Motif lisible pour un champ obligatoire qui n'a pas pu être lu. */
        const pourquoi = (cle, nom) => {
            const mf = malFormes.get(cle);
            return mf
                ? `${nom} « ${mf.valeur} » n'est pas ${mf.forme}`
                : `${nom} absent`;
        };

        // La clé. Sans elle, aucune identité : la ligne est refusée, pas devinée.
        if (!objet.code_etudiant) {
            refus.push({
                ligne: ligne.numero, motif: 'cle_absente',
                message: pourquoi('code_etudiant', 'Numéro étudiant')
            });
            continue;
        }
        if (!objet.nom || !objet.prenom) {
            const quoi = !objet.nom ? pourquoi('nom', 'Nom') : pourquoi('prenom', 'Prénom');
            refus.push({
                ligne: ligne.numero, motif: 'identite_incomplete',
                message: `${quoi} (n° ${objet.code_etudiant})`
            });
            continue;
        }
        if (!objet.code_etape || !objet.version_etape) {
            const quoi = !objet.code_etape
                ? pourquoi('code_etape', "Code étape")
                : pourquoi('version_etape', "Version d'étape");
            refus.push({
                ligne: ligne.numero, motif: 'etape_absente',
                message: `${quoi} (n° ${objet.code_etudiant})`
            });
            continue;
        }

        // Doublon DANS LE FICHIER : cas limite n° 2 du banc — le même numéro sur deux
        // lignes, avec deux noms différents. Le serveur ne le verrait pas comme un
        // doublon (il ferait une création puis une mise à jour), et la dernière ligne
        // gagnerait en silence. On refuse la seconde en nommant la première.
        if (vues.has(objet.code_etudiant)) {
            doublonsInternes.push({
                ligne: ligne.numero, motif: 'doublon_fichier',
                message: `Le numéro ${objet.code_etudiant} figure déjà ligne ${vues.get(objet.code_etudiant)}`
            });
            continue;
        }
        vues.set(objet.code_etudiant, ligne.numero);

        // Une valeur mal formée sur un champ NON obligatoire ne perd pas la ligne :
        // l'étudiant entre, sans cette valeur, et l'écran le dit en avertissement.
        // Les avertissements restent DANS LE NAVIGATEUR : les transmettre ne servirait
        // qu'à faire voyager du texte que le serveur ignorerait.
        for (const [, mf] of malFormes) {
            avertissements.push({
                ligne: ligne.numero,
                message: `${mf.source} : « ${mf.valeur} » n'est pas ${mf.forme} — valeur ignorée`
            });
        }

        preparees.push(objet);
    }

    return { lignes: preparees, refus, doublonsInternes, avertissements };
}

// ═══════════════════════════════════════════════════════════════════════════════
// 7. LECTURE D'UNE FEUILLE — l'enchaînement complet
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Sépare l'en-tête des données et NUMÉROTE les lignes comme le fichier les numérote.
 *
 * ⚠️ Deux pièges mesurés (§0.1, banc §4) :
 *   · la feuille DÉCLARE une plage jusqu'à la ligne 188 pour 148 lignes de données,
 *     et SCODOC 753 pour 62. Un parseur qui compte les lignes déclarées se trompe
 *     de 27 % et de 1 130 % — d'où le rejet des lignes entièrement vides ;
 *   · ce rejet ne doit PAS renuméroter : la ligne 24 du bilan doit être la ligne 24
 *     du fichier, sinon on cherche l'erreur au mauvais endroit. Le numéro est donc
 *     attaché AVANT le filtrage.
 */
export function decouperFeuille(matrice) {
    const brutes = matrice || [];
    if (!brutes.length) return { entetes: [], lignes: [], nbLignesDeclarees: 0 };

    const entetes = (brutes[0] || []).map((c) => String(c ?? ''));
    const lignes = brutes.slice(1)
        .map((cellules, i) => ({ numero: i + 2, cellules: cellules || [] }))
        .filter((l) => l.cellules.some((c) => !estVide(c)));

    return { entetes, lignes, nbLignesDeclarees: brutes.length - 1 };
}

// ═══════════════════════════════════════════════════════════════════════════════
// 7 bis. LE COLLAGE DEPUIS EXCEL — lot 1.2 bis
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Texte collé depuis Excel → matrice, dans la MÊME forme que celle que SheetJS
 * produit. C'est ce qui permet au collage de traverser exactement le même moteur
 * que le fichier : appariement, contrôle de forme, prévisualisation, bilan.
 *
 * ── CE QUE LE COLLAGE CHANGE, ET CE QU'IL NE CHANGE PAS ──────────────────────
 * Excel met la TABULATION entre les colonnes, et un saut de ligne entre les
 * lignes. Rien d'autre n'est garanti :
 *
 *   · TOUT ARRIVE EN TEXTE. Le numéro étudiant devient « 20253727 » — sans
 *     conséquence, les validateurs travaillent déjà sur du texte. Mais la date de
 *     naissance devient ce qu'Excel AFFICHE : « 02/12/2007 » quand la cellule est
 *     formatée en date (les 80 lignes du groupe 1), et le SERIAL BRUT « 39418 »
 *     quand elle ne l'est pas (les 67 du groupe 2). Les deux formes sont déjà
 *     acceptées par `convertirDate` — c'est la raison d'être de ses trois formes.
 *
 *   · UNE CELLULE PEUT CONTENIR UNE TABULATION OU UN SAUT DE LIGNE. Excel
 *     l'entoure alors de guillemets et double les guillemets internes. Un parseur
 *     qui découperait naïvement sur les tabulations DÉCALERAIT toutes les colonnes
 *     suivantes — exactement le genre de décalage silencieux que ce module passe
 *     son temps à refuser. D'où le parseur à état ci-dessous.
 *
 *   · IL N'Y A NI NOM DE FICHIER NI FEUILLE À CHOISIR : le journal reçoit
 *     `mode = 'coller'` et `fichier_nom = null`.
 *
 * Ce qui ne change pas : la ligne d'EN-TÊTE reste obligatoire. Apparier par
 * position parce que l'utilisateur n'a pas copié les en-têtes serait le devineur
 * que le §0.8 interdit — et sur un fichier à deux extractions, ce devineur
 * écrirait des données fausses pour 46 % de l'effectif.
 */
export function matriceDepuisTexte(texte, separateur) {
    const brut = String(texte ?? '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    if (!brut.trim()) return [];

    const sep = separateur || devinerSeparateur(brut);
    const matrice = [];
    let ligne = [];
    let cellule = '';
    let dansGuillemets = false;

    for (let i = 0; i < brut.length; i++) {
        const c = brut[i];

        if (dansGuillemets) {
            if (c === '"') {
                // Guillemet doublé = un guillemet littéral dans la cellule.
                if (brut[i + 1] === '"') { cellule += '"'; i++; }
                else dansGuillemets = false;
            } else {
                cellule += c;
            }
            continue;
        }

        if (c === '"' && cellule === '') { dansGuillemets = true; continue; }
        if (c === sep) { ligne.push(cellule); cellule = ''; continue; }
        if (c === '\n') { ligne.push(cellule); matrice.push(ligne); ligne = []; cellule = ''; continue; }
        cellule += c;
    }
    // La dernière cellule n'est suivie d'aucun séparateur.
    ligne.push(cellule);
    matrice.push(ligne);

    // Une sélection Excel se termine souvent par un saut de ligne : la dernière
    // ligne est alors une cellule vide, qu'il ne faut pas compter comme une donnée.
    while (matrice.length && matrice[matrice.length - 1].every((c) => String(c).trim() === '')) {
        matrice.pop();
    }
    return matrice;
}

/**
 * Le presse-papiers d'Excel utilise la TABULATION. Le point-virgule et la virgule
 * sont acceptés en repli, pour le cas où le texte vient d'un fichier CSV ouvert
 * dans un éditeur — mais la tabulation gagne dès qu'elle est présente : les
 * intitulés APOGEE contiennent des points et des tirets, jamais de tabulation.
 */
function devinerSeparateur(texte) {
    const premiere = texte.split('\n')[0] || '';
    if (premiere.includes('\t')) return '\t';
    if (premiere.includes(';')) return ';';
    if (premiere.includes(',')) return ',';
    return '\t';
}

/**
 * Analyse d'un collage. Même moteur que le fichier, plus un diagnostic propre au
 * collage : quand la première ligne ne porte aucun intitulé connu, c'est presque
 * toujours que l'utilisateur a copié ses lignes SANS la ligne d'en-tête. Le dire
 * vaut mieux que d'annoncer « colonne clé absente », qui ferait chercher au mauvais
 * endroit.
 */
export function analyserCollage(texte, cleLiaison) {
    const matrice = matriceDepuisTexte(texte);
    if (!matrice.length) {
        return { ...analyserFeuille([], cleLiaison), bloquant: 'Rien à analyser : la zone de collage est vide' };
    }

    const analyse = analyserFeuille(matrice, cleLiaison);
    if (analyse.colonnes.some((c) => c.trouvee)) return analyse;

    return {
        ...analyse,
        bloquant: "Aucun intitulé de colonne reconnu sur la première ligne collée. "
                + "Sélectionne aussi la LIGNE DES EN-TÊTES dans Excel : sans elle, les colonnes "
                + "ne peuvent être identifiées que par leur position, et ce fichier ne le permet pas."
    };
}

/**
 * Analyse complète d'une feuille : appariement, contrôle de forme, lignes prêtes.
 * C'est ce que la prévisualisation affiche, et rien ne s'écrit avant.
 */
export function analyserFeuille(matrice, cleLiaison) {
    const { entetes, lignes, nbLignesDeclarees } = decouperFeuille(matrice);
    const appariement = apparierColonnes(entetes, cleLiaison);
    const colonnes = controlerColonnes(appariement.colonnes, lignes);
    const { lignes: pretes, refus, doublonsInternes, avertissements } = preparerLignes(colonnes, lignes);

    const cleRefusee = colonnes.find((c) => c.role === 'cle')?.refusee || false;

    return {
        entetes,
        nbLignesDeclarees,
        nbLignesUtiles: lignes.length,
        colonnes,
        colonnesRefusees: colonnes.filter((c) => c.refusee),
        nonLues: appariement.nonLues,
        inconnues: appariement.inconnues,
        cleManquante: appariement.cleManquante,
        cleRefusee,
        obligatoiresManquants: appariement.obligatoiresManquants,
        lignes: pretes,
        refus: [...refus, ...doublonsInternes],
        avertissements,
        // Bloquant : sans clé de liaison utilisable, ou sans une colonne obligatoire,
        // l'import n'a pas d'objet. Le message est explicite, l'écran ne propose pas
        // d'importer quand même.
        bloquant: appariement.cleManquante || cleRefusee
            ? (appariement.cleManquante
                ? `La colonne clé « ${cleLiaison} » est absente du fichier`
                : `La colonne clé « ${cleLiaison} » ne contient pas des numéros étudiants`)
            : appariement.obligatoiresManquants.length
                ? 'Colonnes obligatoires absentes : ' +
                  appariement.obligatoiresManquants.map((c) => `« ${c.intituleAttendu} »`).join(', ')
                : pretes.length === 0
                    ? 'Aucune ligne exploitable dans cette feuille'
                    : null
    };
}
